import { useCallback, useEffect, useState } from "react";
import { canCompleteScene } from "../content/scenes";
import { calculateChapterScore, getChapterMaxScore } from "../lib/scoring";
import { MAX_AI_THREAD_MESSAGES } from "../types";
import type {
  ChapterId,
  AiMessage,
  AiThreadId,
  EvidenceId,
  FirstAttemptRecord,
  QualityTier,
  MisconceptionTag,
  SceneEvent,
  SceneEventKind,
  SceneId,
  SceneProgress,
  SceneStatus,
  SceneVisualState,
  Screen,
  StoredProgress
} from "../types";

export const STORAGE_KEY = "nature-language-session-v2";
export const LEGACY_STORAGE_KEY = "nature-language-session-v1";
export const MAX_SCENE_EVENTS = 32;

const CHAPTER_IDS: readonly ChapterId[] = ["chapter-1", "chapter-2", "chapter-3", "chapter-4"];
const SCREEN_IDS: readonly Screen[] = ["title", "map", "chapter", "quiz", "report"];
const SCENE_IDS: readonly SceneId[] = [
  "C1-00", "C1-01", "C1-02", "C1-03",
  "C2-00", "C2-01", "C2-03",
  "C3-00", "C3-01", "C3-02", "C3-03", "C3-04", "C3-05",
  "C4-01", "C4-03", "F01", "F02", "F03"
];
const RETIRED_SCENE_REDIRECTS: Readonly<Record<string, SceneId>> = {
  "C2-02": "C2-03",
  "C4-02": "C4-03"
};
const EVIDENCE_IDS: readonly EvidenceId[] = [
  "P01", "P02", "P03", "P04", "P05", "P06",
  "P07", "P08", "P09", "P10", "P11", "P12"
];
const AI_THREAD_IDS: readonly AiThreadId[] = ["chapter-1", "chapter-2", "chapter-3", "chapter-4", "quiz"];
const SCENE_STATUSES: readonly SceneStatus[] = [
  "not-started", "in-progress", "awaiting-evidence", "mastered"
];
const C4_PRODUCTION_ITEM_IDS = ["sow", "zone", "forest", "introduce", "pest", "mountain"] as const;

type StorageReader = Pick<Storage, "getItem">;

function emptyVisualState(): SceneVisualState {
  return { selectedItemIds: [], placedItemIds: {}, controlValues: {} };
}

type SceneEventInput = Omit<SceneEvent, "id" | "createdAt"> & { id?: string; createdAt?: number };

function latestChangedKey(
  previous: Record<string, unknown>,
  next: Record<string, unknown>
): string | undefined {
  const keys = Object.keys(next);
  for (let index = keys.length - 1; index >= 0; index -= 1) {
    const key = keys[index];
    if (key !== undefined && previous[key] !== next[key]) return key;
  }
  return undefined;
}

/** Build a bounded semantic event from a serializable visual-state patch. */
export function sceneEventFromVisualPatch(
  scene: SceneProgress,
  patch: Partial<SceneVisualState>
): SceneEventInput {
  if (patch.placedItemIds) {
    return {
      kind: "drag",
      actionId: "action.item.place",
      result: "pending",
      valueId: "value.placed",
      itemId: latestChangedKey(scene.visualState.placedItemIds, patch.placedItemIds)
    };
  }
  if (patch.selectedItemIds) {
    const added = patch.selectedItemIds.filter((itemId) => !scene.visualState.selectedItemIds.includes(itemId));
    const removed = scene.visualState.selectedItemIds.filter((itemId) => !patch.selectedItemIds?.includes(itemId));
    return {
      kind: "select",
      actionId: "action.item.select",
      result: "pending",
      valueId: "value.selected",
      itemId: added.at(-1) ?? removed.at(-1) ?? patch.selectedItemIds.at(-1)
    };
  }
  return {
    kind: "control-change",
    actionId: "action.control.change",
    result: "pending",
    valueId: "value.adjusted",
    itemId: patch.controlValues
      ? latestChangedKey(scene.visualState.controlValues, patch.controlValues)
      : undefined
  };
}

function eventId(createdAt: number): string {
  return `event-${createdAt}-${Math.random().toString(36).slice(2, 8)}`;
}

export function appendSceneEvent(scene: SceneProgress, input: SceneEventInput): SceneProgress {
  const createdAt = input.createdAt ?? Date.now();
  const next: SceneEvent = {
    ...input,
    id: input.id ?? eventId(createdAt),
    createdAt
  };
  const previous = scene.events.at(-1);
  const duplicateBurst = previous
    && next.kind === "submit"
    && previous.kind === "submit"
    && previous.result === next.result
    && previous.actionId === next.actionId
    && createdAt - previous.createdAt < 100;
  const events = duplicateBurst
    ? [...scene.events.slice(0, -1), next]
    : [...scene.events, next];
  return { ...scene, events: events.slice(-MAX_SCENE_EVENTS) };
}

export function createSceneProgress(sceneId: SceneId): SceneProgress {
  return {
    sceneId,
    status: "not-started",
    masteredRuleIds: [],
    attemptedRuleIds: [],
    firstAttempts: {},
    selectedEvidenceIds: [],
    evidenceViews: [],
    hintCount: 0,
    visualState: emptyVisualState(),
    events: []
  };
}

export function createInitialProgress(): StoredProgress {
  return {
    version: 2,
    route: { screen: "title", chapterId: "chapter-1", sceneId: null },
    scenes: {},
    completedChapters: [],
    chapterScores: {},
    quiz: {
      firstAnswers: {},
      correctedAnswers: {},
      firstCorrectQuestionIds: [],
      masteredQuestionIds: [],
      firstScore: 0,
      masteryScore: 0
    },
    quizFirstScore: 0,
    quizMasteryScore: 0,
    aiAuditCompleted: false,
    metrics: {
      attempts: 0,
      firstTryTotal: 0,
      firstTryCorrect: 0,
      hintsUsed: 0,
      evidenceOpened: 0
    },
    aiThreads: {},
    aiIntroAcknowledged: false,
    soundEnabled: true,
    reducedMotion: false,
    quality: "balanced",
    aiMode: "local"
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === "string"))]
    : [];
}

function evidenceArray(value: unknown): EvidenceId[] {
  const allowed = new Set<string>(EVIDENCE_IDS);
  return stringArray(value).filter((item): item is EvidenceId => allowed.has(item));
}

function nonNegativeInteger(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : 0;
}

const SCENE_EVENT_KINDS: readonly SceneEventKind[] = [
  "scene-enter", "drag", "select", "control-change", "evidence-view", "submit", "reset", "complete"
];

function sanitizeSceneEvents(value: unknown): SceneEvent[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate): SceneEvent[] => {
    if (!isRecord(candidate)) return [];
    if (typeof candidate.id !== "string" || !SCENE_EVENT_KINDS.includes(candidate.kind as SceneEventKind)) return [];
    if (typeof candidate.actionId !== "string" || !candidate.actionId.startsWith("action.")) return [];
    if (candidate.result !== "success" && candidate.result !== "error" && candidate.result !== "pending") return [];
    const event: SceneEvent = {
      id: candidate.id.slice(0, 120),
      kind: candidate.kind as SceneEventKind,
      actionId: candidate.actionId.slice(0, 80),
      result: candidate.result,
      createdAt: nonNegativeInteger(candidate.createdAt)
    };
    if (typeof candidate.valueId === "string" && candidate.valueId.startsWith("value.")) event.valueId = candidate.valueId.slice(0, 80);
    if (typeof candidate.itemId === "string") event.itemId = candidate.itemId.slice(0, 80);
    if (EVIDENCE_IDS.includes(candidate.evidenceId as EvidenceId)) event.evidenceId = candidate.evidenceId as EvidenceId;
    if (["evidence-missing", "concept-confusion", "causality-overstated", "scope-overreach", "expression-unclear"].includes(String(candidate.misconceptionTag))) {
      event.misconceptionTag = candidate.misconceptionTag as SceneEvent["misconceptionTag"];
    }
    return [event];
  }).slice(-MAX_SCENE_EVENTS);
}

function aiMessage(value: unknown): AiMessage | null {
  if (!isRecord(value)) return null;
  if (value.role !== "user" && value.role !== "assistant") return null;
  if (typeof value.id !== "string" || typeof value.text !== "string") return null;
  if (!SCENE_IDS.includes(value.sceneId as SceneId)) return null;
  const evidenceRefs = evidenceArray(value.evidenceRefs);
  const message: AiMessage = {
    id: value.id.slice(0, 120),
    role: value.role,
    text: value.text.slice(0, value.role === "user" ? 300 : 400),
    sceneId: value.sceneId as SceneId,
    evidenceRefs,
    createdAt: nonNegativeInteger(value.createdAt)
  };
  if (value.mode === "online" || value.mode === "fallback") message.mode = value.mode;
  if (["not-configured", "invalid-key", "rate-limited", "timeout", "invalid-context", "model-unavailable", "upstream-error", "invalid-response", "network-error"].includes(String(value.fallbackReason))) {
    message.fallbackReason = value.fallbackReason as AiMessage["fallbackReason"];
  }
  if (value.confidence === "low" || value.confidence === "medium" || value.confidence === "high") message.confidence = value.confidence;
  return message;
}

export function capAiThreadMessages(messages: readonly AiMessage[]): AiMessage[] {
  return messages.slice(-MAX_AI_THREAD_MESSAGES);
}

function sanitizeAiThreads(value: unknown): Partial<Record<AiThreadId, AiMessage[]>> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    AI_THREAD_IDS.flatMap((threadId) => {
      const raw = value[threadId];
      if (!Array.isArray(raw)) return [];
      const messages = raw.flatMap((candidate) => {
        const message = aiMessage(candidate);
        return message ? [message] : [];
      });
      return messages.length > 0 ? [[threadId, capAiThreadMessages(messages)]] : [];
    })
  ) as Partial<Record<AiThreadId, AiMessage[]>>;
}

function readSettings(source: Record<string, unknown>, target: StoredProgress): void {
  if (typeof source.soundEnabled === "boolean") target.soundEnabled = source.soundEnabled;
  if (typeof source.reducedMotion === "boolean") target.reducedMotion = source.reducedMotion;
  if (["high", "balanced", "low"].includes(String(source.quality))) {
    target.quality = source.quality as QualityTier;
  }
  if (source.aiMode === "online" || source.aiMode === "local") target.aiMode = source.aiMode;
}

function sanitizeFirstAttempts(value: unknown): Record<string, FirstAttemptRecord> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).flatMap(([ruleId, candidate]) => {
      if (!isRecord(candidate) || typeof candidate.correct !== "boolean") return [];
      const attemptValue = candidate.value;
      const validValue = attemptValue === null
        || typeof attemptValue === "string"
        || (Array.isArray(attemptValue) && attemptValue.every((item) => typeof item === "string"));
      if (!validValue) return [];
      return [[ruleId, { value: attemptValue as string | string[] | null, correct: candidate.correct }]];
    })
  );
}

function sanitizeVisualState(value: unknown): SceneVisualState {
  if (!isRecord(value)) return emptyVisualState();
  const placedItemIds = isRecord(value.placedItemIds)
    ? Object.fromEntries(
      Object.entries(value.placedItemIds).filter((entry): entry is [string, string] => typeof entry[1] === "string")
    )
    : {};
  const controlValues = isRecord(value.controlValues)
    ? Object.fromEntries(
      Object.entries(value.controlValues).filter(([, item]) =>
        typeof item === "string" || typeof item === "number" || typeof item === "boolean"
      )
    ) as SceneVisualState["controlValues"]
    : {};
  return { selectedItemIds: stringArray(value.selectedItemIds), placedItemIds, controlValues };
}

function sanitizeScene(sceneId: SceneId, value: unknown): SceneProgress {
  const initial = createSceneProgress(sceneId);
  if (!isRecord(value)) return initial;
  return {
    sceneId,
    status: SCENE_STATUSES.includes(value.status as SceneStatus)
      ? value.status as SceneStatus
      : initial.status,
    masteredRuleIds: stringArray(value.masteredRuleIds),
    attemptedRuleIds: stringArray(value.attemptedRuleIds),
    firstAttempts: sanitizeFirstAttempts(value.firstAttempts),
    selectedEvidenceIds: evidenceArray(value.selectedEvidenceIds),
    evidenceViews: evidenceArray(value.evidenceViews),
    hintCount: nonNegativeInteger(value.hintCount),
    visualState: sanitizeVisualState(value.visualState),
    events: sanitizeSceneEvents(value.events)
  };
}

/**
 * v1 只迁移声音、画质、减少动态与 AI 模式。由于正式计分规则已改变，旧章节、
 * 测验和聚合计数不会进入 v2。
 */
export function migrateStoredProgress(value: unknown): StoredProgress {
  const initial = createInitialProgress();
  if (!isRecord(value)) return initial;
  readSettings(value, initial);
  if (value.version !== 2) return initial;

  if (isRecord(value.route)) {
    const screen = SCREEN_IDS.includes(value.route.screen as Screen)
      ? value.route.screen as Screen
      : initial.route.screen;
    const chapterId = CHAPTER_IDS.includes(value.route.chapterId as ChapterId)
      ? value.route.chapterId as ChapterId
      : initial.route.chapterId;
    const storedSceneId = typeof value.route.sceneId === "string" ? value.route.sceneId : "";
    const sceneId = RETIRED_SCENE_REDIRECTS[storedSceneId]
      ?? (SCENE_IDS.includes(storedSceneId as SceneId) ? storedSceneId as SceneId : null);
    initial.route = { screen, chapterId, sceneId };
  }

  if (isRecord(value.scenes)) {
    for (const sceneId of SCENE_IDS) {
      if (sceneId in value.scenes) initial.scenes[sceneId] = sanitizeScene(sceneId, value.scenes[sceneId]);
    }
  }

  const productionScene = initial.scenes["C4-01"];
  if (productionScene) {
    const completedLegacyScene = productionScene.masteredRuleIds.includes("c4.process.observe")
      || C4_PRODUCTION_ITEM_IDS.every((itemId) => productionScene.visualState.placedItemIds[itemId] === itemId);
    if (completedLegacyScene && !productionScene.masteredRuleIds.includes("c4.process.read-p11")) {
      initial.scenes["C4-01"] = {
        ...productionScene,
        evidenceViews: [...new Set([...productionScene.evidenceViews, "P11" as const])],
        masteredRuleIds: [...productionScene.masteredRuleIds, "c4.process.read-p11"],
        attemptedRuleIds: productionScene.attemptedRuleIds.includes("c4.process.read-p11")
          ? productionScene.attemptedRuleIds
          : [...productionScene.attemptedRuleIds, "c4.process.read-p11"],
        firstAttempts: productionScene.firstAttempts["c4.process.read-p11"]
          ? productionScene.firstAttempts
          : {
            ...productionScene.firstAttempts,
            "c4.process.read-p11": { value: "P11", correct: true }
          }
      };
    }
  }

  initial.completedChapters = stringArray(value.completedChapters)
    .filter((item): item is ChapterId => CHAPTER_IDS.includes(item as ChapterId));
  if (isRecord(value.chapterScores)) {
    for (const chapterId of CHAPTER_IDS) {
      const score = value.chapterScores[chapterId];
      if (typeof score === "number" && Number.isFinite(score)) {
        initial.chapterScores[chapterId] = Math.max(0, Math.min(getChapterMaxScore(chapterId), score));
      }
    }
  }

  if (isRecord(value.quiz)) {
    const firstAnswers = isRecord(value.quiz.firstAnswers) ? value.quiz.firstAnswers : {};
    const correctedAnswers = isRecord(value.quiz.correctedAnswers) ? value.quiz.correctedAnswers : {};
    const validAnswers = (answers: Record<string, unknown>) => Object.fromEntries(
      Object.entries(answers).filter(([, answer]) =>
        typeof answer === "string"
        || (Array.isArray(answer) && answer.every((item) => typeof item === "string"))
      )
    ) as Record<string, string | string[]>;
    initial.quiz = {
      firstAnswers: validAnswers(firstAnswers),
      correctedAnswers: validAnswers(correctedAnswers),
      firstCorrectQuestionIds: stringArray(value.quiz.firstCorrectQuestionIds),
      masteredQuestionIds: stringArray(value.quiz.masteredQuestionIds),
      firstScore: Math.min(5, nonNegativeInteger(value.quiz.firstScore)),
      masteryScore: Math.min(5, nonNegativeInteger(value.quiz.masteryScore))
    };
  }
  initial.quizFirstScore = initial.quiz.firstScore;
  initial.quizMasteryScore = initial.quiz.masteryScore;
  initial.aiAuditCompleted = value.aiAuditCompleted === true;

  if (isRecord(value.metrics)) {
    initial.metrics = {
      attempts: nonNegativeInteger(value.metrics.attempts),
      firstTryTotal: nonNegativeInteger(value.metrics.firstTryTotal),
      firstTryCorrect: nonNegativeInteger(value.metrics.firstTryCorrect),
      hintsUsed: nonNegativeInteger(value.metrics.hintsUsed),
      evidenceOpened: nonNegativeInteger(value.metrics.evidenceOpened)
    };
  }
  initial.aiThreads = sanitizeAiThreads(value.aiThreads);
  initial.aiIntroAcknowledged = value.aiIntroAcknowledged === true;
  return initial;
}

function parseProgress(raw: string | null): unknown {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

export function loadProgress(storage: StorageReader = localStorage): StoredProgress {
  const current = parseProgress(storage.getItem(STORAGE_KEY));
  if (current !== undefined) return migrateStoredProgress(current);
  return migrateStoredProgress(parseProgress(storage.getItem(LEGACY_STORAGE_KEY)));
}

function addUnique<T>(items: T[], item: T): T[] {
  return items.includes(item) ? items : [...items, item];
}

export function getChapterForScene(sceneId: SceneId): ChapterId | undefined {
  if (sceneId.startsWith("C1-")) return "chapter-1";
  if (sceneId.startsWith("C2-")) return "chapter-2";
  if (sceneId.startsWith("C3-")) return "chapter-3";
  if (sceneId.startsWith("C4-")) return "chapter-4";
  return undefined;
}

function getScreenForScene(sceneId: SceneId): Screen {
  if (sceneId === "F03") return "report";
  if (sceneId.startsWith("F")) return "quiz";
  return "chapter";
}

function updateScene(
  progress: StoredProgress,
  sceneId: SceneId,
  updater: (scene: SceneProgress) => SceneProgress
): StoredProgress {
  const scene = progress.scenes[sceneId] ?? createSceneProgress(sceneId);
  return { ...progress, scenes: { ...progress.scenes, [sceneId]: updater(scene) } };
}

/** 纯函数版本供状态归约器和测试使用；只清视觉布局，不清作答历史或掌握记录。 */
export function resetSceneLayoutState(progress: StoredProgress, sceneId: SceneId): StoredProgress {
  return updateScene(progress, sceneId, (scene) => appendSceneEvent({
    ...scene,
    visualState: emptyVisualState()
  }, {
    kind: "reset",
    actionId: "action.task.reset",
    result: "success",
    valueId: "value.none"
  }));
}

/** 纯函数作答归约：首次快照只写一次，后续订正可增加掌握规则但不会覆盖首次表现。 */
export function recordRuleAttemptState(
  progress: StoredProgress,
  sceneId: SceneId,
  ruleId: string,
  correct: boolean,
  value: FirstAttemptRecord["value"] = null,
  misconceptionTag: MisconceptionTag = "concept-confusion"
): StoredProgress {
  const existing = progress.scenes[sceneId] ?? createSceneProgress(sceneId);
  const isFirstTry = !existing.attemptedRuleIds.includes(ruleId);
  const hasPriorSubmission = existing.events.some((event) => event.kind === "submit");
  const next = updateScene(progress, sceneId, (scene) => appendSceneEvent({
    ...scene,
    status: scene.status === "not-started" ? "in-progress" : scene.status,
    attemptedRuleIds: addUnique(scene.attemptedRuleIds, ruleId),
    masteredRuleIds: correct ? addUnique(scene.masteredRuleIds, ruleId) : scene.masteredRuleIds,
    firstAttempts: isFirstTry
      ? { ...scene.firstAttempts, [ruleId]: { value, correct } }
      : scene.firstAttempts
  }, {
    kind: "submit",
    actionId: hasPriorSubmission ? "action.task.retry" : "action.task.attempt",
    result: correct ? "success" : "error",
    valueId: correct && hasPriorSubmission ? "value.corrected" : "value.submitted",
    itemId: ruleId,
    misconceptionTag: correct ? undefined : misconceptionTag
  }));
  return {
    ...next,
    metrics: {
      ...next.metrics,
      attempts: next.metrics.attempts + 1,
      firstTryTotal: next.metrics.firstTryTotal + (isFirstTry ? 1 : 0),
      firstTryCorrect: next.metrics.firstTryCorrect + (isFirstTry && correct ? 1 : 0)
    }
  };
}

export function useProgress() {
  const [progress, setProgress] = useState<StoredProgress>(loadProgress);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const setRoute = useCallback((screen: Screen, chapterId?: ChapterId, sceneId?: SceneId | null) => {
    setProgress((current) => ({
      ...current,
      route: {
        screen,
        chapterId: chapterId ?? current.route.chapterId,
        sceneId: sceneId === undefined ? current.route.sceneId : sceneId
      }
    }));
  }, []);

  const startScene = useCallback((sceneId: SceneId, chapterId?: ChapterId) => {
    setProgress((current) => {
      const sceneChapterId = chapterId ?? getChapterForScene(sceneId) ?? current.route.chapterId;
      const routed = {
        ...current,
        route: { screen: getScreenForScene(sceneId), chapterId: sceneChapterId, sceneId }
      };
      return updateScene(routed, sceneId, (scene) => appendSceneEvent({
        ...scene,
        status: scene.status === "not-started" ? "in-progress" : scene.status
      }, {
        kind: "scene-enter",
        actionId: "action.scene.enter",
        result: "pending",
        valueId: "value.none"
      }));
    });
  }, []);

  const setSceneStatus = useCallback((sceneId: SceneId, status: SceneStatus) => {
    setProgress((current) => updateScene(current, sceneId, (scene) => ({ ...scene, status })));
  }, []);

  const completeScene = useCallback((sceneId: SceneId) => {
    setProgress((current) => canCompleteScene(current, sceneId)
      ? updateScene(current, sceneId, (scene) => appendSceneEvent({ ...scene, status: "mastered" }, {
        kind: "complete",
        actionId: "action.task.attempt",
        result: "success",
        valueId: "value.submitted"
      }))
      : current);
  }, []);

  const recordRuleAttempt = useCallback((
    sceneId: SceneId,
    ruleId: string,
    correct: boolean,
    value: FirstAttemptRecord["value"] = null,
    misconceptionTag: MisconceptionTag = "concept-confusion"
  ) => {
    setProgress((current) => recordRuleAttemptState(current, sceneId, ruleId, correct, value, misconceptionTag));
  }, []);

  /** 旧章节组件的兼容入口；新场景应使用 recordRuleAttempt。 */
  const recordAttempt = useCallback((correct: boolean, isFirstTry: boolean) => {
    setProgress((current) => ({
      ...current,
      metrics: {
        ...current.metrics,
        attempts: current.metrics.attempts + 1,
        firstTryTotal: current.metrics.firstTryTotal + (isFirstTry ? 1 : 0),
        firstTryCorrect: current.metrics.firstTryCorrect + (isFirstTry && correct ? 1 : 0)
      }
    }));
  }, []);

  const setSelectedEvidenceIds = useCallback((sceneId: SceneId, evidenceIds: EvidenceId[]) => {
    setProgress((current) => updateScene(current, sceneId, (scene) => ({
      ...scene,
      selectedEvidenceIds: [...new Set(evidenceIds)]
    })));
  }, []);

  const recordEvidence = useCallback((evidenceId?: EvidenceId, sceneId?: SceneId) => {
    setProgress((current) => {
      const withMetric = {
        ...current,
        metrics: { ...current.metrics, evidenceOpened: current.metrics.evidenceOpened + 1 }
      };
      if (!evidenceId || !sceneId) return withMetric;
      return updateScene(withMetric, sceneId, (scene) => appendSceneEvent({
        ...scene,
        evidenceViews: addUnique(scene.evidenceViews, evidenceId)
      }, {
        kind: "evidence-view",
        actionId: "action.evidence.open",
        result: "success",
        valueId: "value.selected",
        evidenceId
      }));
    });
  }, []);

  const recordHint = useCallback((sceneId?: SceneId) => {
    setProgress((current) => {
      const withMetric = {
        ...current,
        metrics: { ...current.metrics, hintsUsed: current.metrics.hintsUsed + 1 }
      };
      return sceneId
        ? updateScene(withMetric, sceneId, (scene) => ({ ...scene, hintCount: scene.hintCount + 1 }))
        : withMetric;
    });
  }, []);

  const patchSceneVisualState = useCallback((sceneId: SceneId, patch: Partial<SceneVisualState>) => {
    setProgress((current) => updateScene(current, sceneId, (scene) => {
      const event = sceneEventFromVisualPatch(scene, patch);
      return appendSceneEvent({
        ...scene,
        visualState: { ...scene.visualState, ...patch }
      }, event);
    }));
  }, []);

  const recordSceneEvent = useCallback((sceneId: SceneId, event: SceneEventInput) => {
    setProgress((current) => updateScene(current, sceneId, (scene) => appendSceneEvent(scene, event)));
  }, []);

  /** 重置卡片位置时保留 attemptedRuleIds、firstAttempts 与 masteredRuleIds。 */
  const resetSceneLayout = useCallback((sceneId: SceneId) => {
    setProgress((current) => resetSceneLayoutState(current, sceneId));
  }, []);

  const completeChapter = useCallback((id: ChapterId, legacyScore?: number) => {
    setProgress((current) => {
      const derivedScore = calculateChapterScore(current, id);
      const score = legacyScore === undefined
        ? derivedScore
        : Math.min(getChapterMaxScore(id), Math.max(derivedScore, legacyScore));
      return {
        ...current,
        completedChapters: addUnique(current.completedChapters, id),
        chapterScores: { ...current.chapterScores, [id]: score }
      };
    });
  }, []);

  const saveQuizAnswer = useCallback((
    questionId: string,
    answer: string | string[],
    correct: boolean,
    corrected = false
  ) => {
    setProgress((current) => {
      const alreadyAttempted = questionId in current.quiz.firstAnswers;
      const firstAnswers = alreadyAttempted
        ? current.quiz.firstAnswers
        : { ...current.quiz.firstAnswers, [questionId]: answer };
      const firstCorrectQuestionIds = !alreadyAttempted && correct
        ? addUnique(current.quiz.firstCorrectQuestionIds, questionId)
        : current.quiz.firstCorrectQuestionIds;
      const masteredQuestionIds = correct
        ? addUnique(current.quiz.masteredQuestionIds, questionId)
        : current.quiz.masteredQuestionIds;
      const correctedAnswers = corrected
        ? { ...current.quiz.correctedAnswers, [questionId]: answer }
        : current.quiz.correctedAnswers;
      const firstScore = Math.min(5, firstCorrectQuestionIds.length);
      const masteryScore = Math.min(5, masteredQuestionIds.length);
      return {
        ...current,
        quiz: {
          firstAnswers,
          correctedAnswers,
          firstCorrectQuestionIds,
          masteredQuestionIds,
          firstScore,
          masteryScore
        },
        quizFirstScore: firstScore,
        quizMasteryScore: masteryScore
      };
    });
  }, []);

  /** 旧测验组件的兼容入口。 */
  const saveQuiz = useCallback((firstScore: number, masteryScore: number) => {
    setProgress((current) => ({
      ...current,
      quiz: {
        ...current.quiz,
        firstScore: Math.min(5, Math.max(0, firstScore)),
        masteryScore: Math.min(5, Math.max(0, masteryScore))
      },
      quizFirstScore: Math.min(5, Math.max(0, firstScore)),
      quizMasteryScore: Math.min(5, Math.max(0, masteryScore))
    }));
  }, []);

  const setAiAuditCompleted = useCallback((completed: boolean) => {
    setProgress((current) => ({ ...current, aiAuditCompleted: completed }));
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean) => {
    setProgress((current) => ({ ...current, soundEnabled: enabled }));
  }, []);

  const setReducedMotion = useCallback((enabled: boolean) => {
    setProgress((current) => ({ ...current, reducedMotion: enabled }));
  }, []);

  const setQuality = useCallback((quality: QualityTier) => {
    setProgress((current) => ({ ...current, quality }));
  }, []);

  const setAiMode = useCallback((aiMode: "online" | "local") => {
    setProgress((current) => ({ ...current, aiMode }));
  }, []);

  const setAiThread = useCallback((threadId: AiThreadId, messages: AiMessage[]) => {
    setProgress((current) => ({
      ...current,
      aiThreads: {
        ...current.aiThreads,
        [threadId]: capAiThreadMessages(messages)
      }
    }));
  }, []);

  const acknowledgeAiIntro = useCallback(() => {
    setProgress((current) => current.aiIntroAcknowledged
      ? current
      : { ...current, aiIntroAcknowledged: true });
  }, []);

  const resetProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    setProgress(createInitialProgress());
  }, []);

  return {
    progress,
    setRoute,
    startScene,
    setSceneStatus,
    completeScene,
    recordRuleAttempt,
    recordAttempt,
    setSelectedEvidenceIds,
    recordEvidence,
    recordHint,
    patchSceneVisualState,
    resetSceneLayout,
    completeChapter,
    saveQuizAnswer,
    saveQuiz,
    setAiAuditCompleted,
    setSoundEnabled,
    setReducedMotion,
    setQuality,
    setAiMode,
    setAiThread,
    acknowledgeAiIntro,
    recordSceneEvent,
    resetProgress
  };
}
